import { createContext, useContext, useMemo } from "react";
import { fetchPage, Page, PageProps } from "@zapier/builder.io-kit";
import { getOrCreateApolloClient } from "@/apollo";
import { plansQuery } from "@/components/builder/shared/usePlans";
import { CatalogProducts, getCatalogProducts } from "@/utils";
import { Plan, PlanType } from "@/types";

type PricingPageContextValues = {
  products?: CatalogProducts;
  planStacks: Array<{
    id: string;
    planType: PlanType;
    plans: Plan[];
  }>;
};

const PricingPageContext = createContext<PricingPageContextValues>({
  planStacks: [],
});
export const usePricingPageContext = () => useContext(PricingPageContext);

type Props = {
  content: PageProps["content"];
  products: CatalogProducts;
  planStacks: any;
};

export default function LoggedOutContainer({
  content,
  products,
  planStacks,
}: Props) {
  const value = useMemo(
    () => ({ products, planStacks }),
    [products, planStacks]
  );

  return (
    <PricingPageContext.Provider value={value}>
      <Page content={content} serviceName="revenue" />
    </PricingPageContext.Provider>
  );
}

export async function getStaticProps() {
  let data = {};
  let content = null;

  const apolloClient = getOrCreateApolloClient(null);
  try {
    const plansRequest = apolloClient.query({
      query: plansQuery,
      variables: { accountId: null },
    });

    const pageRequest = fetchPage({
      model: "pricing-page",
      url: "/pricing",
      /**
       *  Builder.io documentation -
       *    - staleCacheSeconds: https://www.builder.io/c/docs/content-api#code-stale-cache-seconds-code
       *    - cacheSeconds: https://www.builder.io/c/docs/content-api#code-cache-seconds-code
       * */
      staleCacheSeconds: 86400,
      cacheSeconds: 60,
    });

    // Get all of the data we need for our initial render
    const [products, pageResult, plansResult] = await Promise.all([
      getCatalogProducts(),
      pageRequest,
      plansRequest,
    ]);

    content = pageResult;

    if (!content.data) {
      return {
        notFound: true,
      };
    }

    data = {
      products,
      planStacks: plansResult.data?.planStacks,
    };
  } catch (e) {
    console.error(e);
  }

  return {
    revalidate: 60,
    props: {
      content,
      ...data,
    },
  };
}
